import React from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

import useRestaurantOnboardingDataQuery from './use-restaurant-onboarding-data-query';
import { activeRestaurantOnboardingStateAtom } from '.';
import robotAnimationData from './robot-animation.json';
import startIconAnimationData from './star-icon-animation-loader.json';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });
import { useSetAtom } from 'jotai';
import { TextEffect } from '../../motion-primitives/text-effect';

import { cn } from '@restworld/utils-common';
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface RestaurantOnboardingAiMagicLoadingProps {
  contentLabels?: {
    loading: string;
    action: string;
    so: string;
    save_your_time: string;
  };
}

// Lock the loader to the viewport so the robot + headings always sit centered
// and never trigger a vertical scroll on small mobile screens (Lottie sources
// render at their natural SVG size otherwise).
const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col gap-4 text-center items-center justify-center w-full relative',
      'min-h-[100dvh] md:min-h-0 md:h-full',
      className
    )}
    {...props}
  />
);

const StarLottieWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-20 h-20 md:w-24 md:h-24', className)} {...props} />
);

const RobotLottieWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-48 h-48 md:w-56 md:h-56', className)} {...props} />
);

const Heading = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h4 className={cn('self-stretch text-neutral-700 text-xl font-semibold', className)} {...props} />
);

const SubTextContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch text-center justify-start', className)} {...props} />
);

const SubText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-200 text-base font-medium', className)} {...props} />
);

const BoldSubText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-200 text-base font-bold', className)} {...props} />
);

const RestaurantOnboardingAiMagicLoading: React.FC<RestaurantOnboardingAiMagicLoadingProps> = ({ contentLabels }) => {
  const { t } = useTranslation('restaurant-onboarding');
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);

  const query = useRestaurantOnboardingDataQuery();

  // When data is already cached (e.g. user navigated back), the queryFn won't re-run
  // so the UI would stay stuck on the loading screen. Transition immediately on cache hit.
  React.useEffect(() => {
    if (query.isSuccess && query.data) {
      setActiveUI('restaurant_onboarding_form');
    }
  }, [query.isSuccess, query.data, setActiveUI]);

  return (
    <Container>
      <StarLottieWrapper>
        <Lottie loop animationData={startIconAnimationData} />
      </StarLottieWrapper>

      <Heading>
        <TextEffect per="char" preset="fade">
          {contentLabels?.loading || t('wait_just_a_couple_of_seconds')}
        </TextEffect>
      </Heading>
      <SubTextContainer>
        <SubText>
          <TextEffect per="char" preset="fade">
            {contentLabels?.action || t('we_are_searching_info_on_your_venue')}
          </TextEffect>
        </SubText>
        <div className="flex self-stretch text-center justify-center gap-1">
          <SubText>
            <TextEffect per="char" preset="fade">
              {contentLabels?.so || t('so')}
            </TextEffect>
          </SubText>
          <BoldSubText>
            <TextEffect per="char" preset="fade">
              {contentLabels?.save_your_time || t('you_dont_have_to_input_them')}
            </TextEffect>
          </BoldSubText>
        </div>
      </SubTextContainer>
      <RobotLottieWrapper>
        <Lottie loop animationData={robotAnimationData} />
      </RobotLottieWrapper>
    </Container>
  );
};

export default RestaurantOnboardingAiMagicLoading;
